import type { Activity } from './activity'

export type RunStatus = 'running' | 'waiting' | 'completed' | 'partial' | 'cancelled' | 'failed'

export const RUN_STATUSES: readonly RunStatus[] = [
  'running',
  'waiting',
  'completed',
  'partial',
  'cancelled',
  'failed',
]

export type RunEntryStep = 'queued' | 'running' | 'done' | 'failed'

export const RUN_ENTRY_STEPS: readonly RunEntryStep[] = ['queued', 'running', 'done', 'failed']

export interface RunEntry {
  readonly id: string
  readonly step: RunEntryStep
  readonly attempts: number
  readonly willRetry?: boolean
  readonly errorCode?: string
  readonly startedAt?: string
  readonly completedAt?: string
}

export interface SetupRun<Stage extends string = string, Entry extends RunEntry = RunEntry> {
  readonly id: string
  readonly brandId: string
  readonly status: RunStatus
  readonly stage: Stage
  readonly entries: readonly Entry[]
  readonly errorCode?: string
  readonly createdAt: string | null
  readonly updatedAt: string | null
  readonly completedAt?: string | null
  readonly cancelledAt?: string | null
}

const LIVE_RUN_STATUSES: readonly RunStatus[] = ['running']

/** Statuses of a run that has not ended: live, or waiting for a person. */
export const OPEN_RUN_STATUSES: readonly RunStatus[] = ['running', 'waiting']

export const RUN_STALE_AFTER_MS = 10 * 60 * 1000

export const RUN_TIMED_OUT_ERROR_CODE = 'run_timed_out'

function runLastChangedAt(run: SetupRun): number | null {
  const stamp = run.updatedAt ?? run.createdAt
  if (stamp === null) return null
  const time = Date.parse(stamp)
  return Number.isNaN(time) ? null : time
}

/** The moment a running run turns stale, in epoch milliseconds; `null` when it is not running or carries no time. */
export function runStaleAt(
  run: SetupRun,
  staleAfterMs: number = RUN_STALE_AFTER_MS,
): number | null {
  if (run.status !== 'running') return null
  const changedAt = runLastChangedAt(run)
  return changedAt === null ? null : changedAt + staleAfterMs
}

/** The run as the app shows it: a stale running run reads as failed with {@link RUN_TIMED_OUT_ERROR_CODE}. */
export function withRunTimeout<R extends SetupRun>(
  run: R,
  now: number,
  staleAfterMs: number = RUN_STALE_AFTER_MS,
): R {
  const staleAt = runStaleAt(run, staleAfterMs)
  if (staleAt === null || now < staleAt) return run
  return { ...run, status: 'failed', errorCode: RUN_TIMED_OUT_ERROR_CODE }
}

export function isRunLive(run: SetupRun | null | undefined): boolean {
  return run !== null && run !== undefined && LIVE_RUN_STATUSES.includes(run.status)
}

/** Whether the run waits for a person's answer. */
export function isRunWaiting(run: SetupRun | null | undefined): boolean {
  return run !== null && run !== undefined && run.status === 'waiting'
}

/** Whether the run has not ended: it is live or waits for a person. */
export function isRunOpen(run: SetupRun | null | undefined): boolean {
  return run !== null && run !== undefined && OPEN_RUN_STATUSES.includes(run.status)
}

export function isRunFailed(run: SetupRun): boolean {
  return run.status === 'failed' || run.status === 'cancelled'
}

export function isRunSettled(run: SetupRun): boolean {
  return run.status === 'completed' || run.status === 'partial'
}

export function runActivity(run: SetupRun | null): Activity {
  if (run === null) return 'idle'
  if (isRunLive(run)) return 'working'
  if (isRunWaiting(run)) return 'attention'
  return isRunFailed(run) ? 'failed' : 'done'
}
